import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, FileWarning, ShieldAlert, Stamp } from "lucide-react";
import { fmtD } from "@/lib/platform";
import { RISK_BAR, scoreTone } from "@/lib/vendors";
import { Panel } from "@/components/rights/RightsBadges";
import { ContractBadge, RiskBadge } from "@/components/vendors/VendorBadges";

const TIERS = ["High", "Medium", "Low"];

const Kpi = ({ icon: Icon, label, value, sub, tone = "text-[#003366]", testId, delay }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 rr-rise" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <Icon className={`w-4 h-4 ${tone}`} />
    </div>
    <div className={`mt-3 text-3xl font-bold font-mono tracking-tight ${tone}`} data-testid={`${testId}-value`}>{value}</div>
    <div className="text-xs text-slate-500 mt-1">{sub}</div>
  </div>
);

const Distribution = ({ title, counts, total, testId }) => (
  <div data-testid={testId}>
    <div className="flex justify-between text-[11px] text-slate-500 mb-1.5"><span className="font-semibold uppercase tracking-wider">{title}</span><span>{total} vendors</span></div>
    <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
      {TIERS.map((t) => counts[t] > 0 && (
        <div key={t} className={`${RISK_BAR[t]} transition-[width] duration-700`} style={{ width: `${(counts[t] / Math.max(total, 1)) * 100}%` }} />
      ))}
    </div>
    <div className="flex gap-4 mt-1.5 text-[11px]">
      {TIERS.map((t) => <span key={t} className="text-slate-600"><span className={`inline-block w-2 h-2 rounded-full mr-1 ${RISK_BAR[t]}`} />{t} <b className="font-mono">{counts[t]}</b></span>)}
    </div>
  </div>
);

const MiniList = ({ items, empty, right, testId }) => (
  <ul className="divide-y divide-slate-100" data-testid={testId}>
    {items.length === 0 && <li className="text-xs text-slate-500 py-3">{empty}</li>}
    {items.map((v) => (
      <li key={v.id} className="py-2.5 flex items-center gap-3">
        <Link to={`/vendors/${v.id}`} className="flex-1 min-w-0 group" data-testid={`${testId}-${v.id}`}>
          <div className="text-sm font-medium text-[#003366] group-hover:text-[#008080] truncate">{v.name}</div>
          <div className="text-[11px] text-slate-500 truncate"><span className="font-mono text-accent-sm">{v.vendor_id}</span> · {v.service_type}</div>
        </Link>
        {right(v)}
      </li>
    ))}
  </ul>
);

export const VendorDashboard = ({ d }) => {
  if (!d) return <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />;
  return (
    <div className="space-y-6" data-testid="vendor-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi icon={ShieldAlert} label="High risk vendors" value={d.by_final_risk.High} tone={d.by_final_risk.High ? "text-red-600" : "text-slate-400"}
             sub={`${d.automatic_high} auto-high · ${d.total} active vendors`} testId="vkpi-high" delay={0} />
        <Kpi icon={AlertTriangle} label="Assessments overdue" value={d.assessments_overdue} tone={d.assessments_overdue ? "text-red-600" : "text-slate-400"}
             sub={`${d.assessments_due_30} due in 30 days · ${d.never_assessed} never assessed`} testId="vkpi-overdue" delay={60} />
        <Kpi icon={FileWarning} label="Contracts at risk" value={d.by_contract_status.EXPIRING + d.by_contract_status.EXPIRED}
             tone="text-amber-600" sub={`${d.by_contract_status.EXPIRED} expired · ${d.by_contract_status.NOT_SIGNED} not signed`} testId="vkpi-contracts" delay={120} />
        <Kpi icon={CalendarClock} label="Avg assessment score" value={d.avg_assessment_score == null ? "—" : `${d.avg_assessment_score}%`}
             tone={scoreTone(d.avg_assessment_score)} sub="Latest approved assessments" testId="vkpi-score" delay={180} />
        <Kpi icon={Stamp} label="Awaiting approval" value={d.pending_approval} tone="text-purple-700"
             sub={`${d.open_tasks} open remediation tasks`} testId="vkpi-approvals" delay={240} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <Panel className="xl:col-span-7" eyebrow="Risk model" title="Inherent vs final risk" testId="vendor-risk-distribution">
          <div className="space-y-5">
            <Distribution title="Inherent risk" counts={d.by_inherent_risk} total={d.total} testId="dist-inherent" />
            <Distribution title="Final risk (after assessment)" counts={d.by_final_risk} total={d.total} testId="dist-final" />
          </div>
          <div className="mt-6">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Hosting × final risk</div>
            <table className="w-full text-xs" data-testid="vendor-hosting-matrix">
              <thead><tr className="text-slate-500"><th className="text-left font-medium py-1">Hosting</th>{TIERS.map((t) => <th key={t} className="font-medium">{t}</th>)}</tr></thead>
              <tbody>
                {Object.entries(d.by_hosting).map(([h, row]) => (
                  <tr key={h} className="border-t border-slate-100">
                    <td className="py-2 text-slate-700">{h === "INDIA" ? "India" : h === "OVERSEAS" ? "Overseas" : "Hybrid"}</td>
                    {TIERS.map((t) => (
                      <td key={t} className="text-center">
                        <span className={`inline-flex w-8 h-7 items-center justify-center rounded font-mono font-semibold ${
                          row[t] ? `${RISK_BAR[t]} text-white` : "bg-slate-50 text-slate-300"}`}>{row[t]}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
        <Panel className="xl:col-span-5" eyebrow="Highest residual risk" title="Top risk vendors" testId="vendor-top-risk">
          <MiniList items={d.top_risk} empty="No vendors yet." testId="top-risk"
                    right={(v) => <div className="text-right"><RiskBadge level={v.risk_level} /><div className="text-[10px] font-mono text-slate-500 mt-0.5">{v.inherent} → {v.residual}</div></div>} />
        </Panel>
        <Panel className="xl:col-span-6" eyebrow="Contract controls" title="Contracts expiring or expired" testId="vendor-expiring">
          <MiniList items={d.expiring} empty="No contracts expiring in the next 60 days." testId="expiring"
                    right={(v) => <div className="text-right"><ContractBadge status={v.contract_status} label={v.contract_status === "EXPIRED" ? "Expired" : "Expiring"} />
                      <div className="text-[10px] font-mono text-slate-500 mt-0.5">{fmtD(v.contract_expiry)}</div></div>} />
        </Panel>
        <Panel className="xl:col-span-6" eyebrow="Reassessment cycle" title="Assessments overdue" testId="vendor-overdue">
          <MiniList items={d.overdue_list} empty="All vendor assessments are within their cycle." testId="overdue"
                    right={(v) => <div className="text-right text-[11px] text-red-600 font-mono">due {fmtD(v.next_assessment_due)}</div>} />
        </Panel>
      </div>
    </div>
  );
};
